import React, { useState, useEffect } from "react";
import AOS from "aos";
import "aos/dist/aos.css";
import {
  Box,
  Container,
  Typography,
  Grid,
  TextField,
  Button,
  IconButton,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { Instagram, Facebook, Email, Language } from "@mui/icons-material";
import { SOCIALS } from "../../constants/socials";
import Navbar from "../../components/Navbar";
import Footer from "../../components/Footer";

const Contact = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  useEffect(() => {
    AOS.init({
      duration: 800,
      easing: "ease-out",
      once: true,
      offset: 100,
    });
  }, []);

  const [formData, setFormData] = useState({
    fullName: "",
    phone: "",
    email: "",
    message: "",
  });

  const handleInputChange = (field) => (event) => {
    setFormData({
      ...formData,
      [field]: event.target.value,
    });
  };

  const handleSubmit = () => {
    console.log("Form submitted:", formData);
    // Handle form submission logic here
  };

  const socialLinks = [
    { icon: Language, href: SOCIALS.google, label: "Google Business" },
    { icon: Instagram, href: SOCIALS.instagram, label: "Instagram" },
    { icon: Facebook, href: SOCIALS.facebook, label: "Facebook" },
    { icon: Email, href: `mailto:${SOCIALS.email}`, label: "Email" },
  ];

  return (
    <>
      <Navbar />
      <Box
        sx={{
          minHeight: "100vh",
          py: { xs: 8, md: 12 },
          boxSizing: "border-box"
        }}
      >
        <Container maxWidth="xl">
          <Grid container spacing={8} alignItems="center">
            {/* Left Section - Content */}
            <Grid item size={{ xs: 12, md: 6 }}>
              <Box sx={{ pr: { md: 4 } }}>
                {/* Category Label */}
                <Typography
                  data-aos="fade-up"
                  sx={{
                    color: theme.palette.text.secondary,
                    fontFamily: "Monument-Private",
                    fontWeight: 500,
                    letterSpacing: 2,
                    fontSize: "0.75rem",
                    mb: 2,
                    textTransform: "uppercase",
                  }}
                >
                  CONTACT
                </Typography>

                {/* Main Heading */}
                <Typography
                  variant="h1"
                  data-aos="fade-up"
                  data-aos-delay="200"
                  sx={{
                    fontFamily: "Monument-Private",
                    fontSize: {
                      xs: "3rem",
                      sm: "4rem",
                      md: "5rem",
                      lg: "6rem",
                    },
                    fontWeight: 700,
                    lineHeight: 0.9,
                    color: "#000",
                    mb: 4,
                  }}
                >
                  Let's
                  <br />
                  connect
                </Typography>

                {/* Description */}
                <Typography
                  variant="body1"
                  data-aos="fade-up"
                  data-aos-delay="400"
                  sx={{
                    color: theme.palette.text.secondary,
                    fontSize: "1.1rem",
                    lineHeight: 1.6,
                    mb: 6,
                    maxWidth: "400px",
                  }}
                >
                  Fill out this form & we'll get back you asap
                </Typography>

                {/* Social Links */}
                <Box sx={{ display: "flex", gap: 2 }} data-aos="fade-up" data-aos-delay="600">
                  {socialLinks.map((social, index) => {
                    const IconComponent = social.icon;
                    return (
                      <IconButton
                        key={index}
                        href={social.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{
                          width: 56,
                          height: 56,
                          border: "1px solid rgba(0, 0, 0, 0.1)",
                          borderRadius: "50%",
                          color: theme.palette.text.primary,
                          transition: "all 0.3s ease",
                          "&:hover": {
                            backgroundColor: theme.palette.text.primary,
                            color: "white",
                            transform: "translateY(-2px)",
                            boxShadow: "0 4px 15px rgba(0,0,0,0.15)",
                          },
                        }}
                      >
                        <IconComponent />
                      </IconButton>
                    );
                  })}
                </Box>
              </Box>
            </Grid>

            {/* Right Section - Form */}
            <Grid item size={{ xs: 12, md: 6 }}>
              <Box
                data-aos="fade-up"
                data-aos-delay="300"
                sx={{
                  backgroundColor: "white",
                  borderRadius: 6,
                  p: { xs: 4, md: 6 },
                  boxShadow: "0 4px 20px rgba(0, 0, 0, 0.08)",
                  border: "1px solid rgba(0, 0, 0, 0.05)",
                }}
              >
                <form
                  name="contact"
                  method="POST"
                  data-netlify="true"
                  netlify-honeypot="bot-field"
                  style={{ margin: 0 }}
                >
                  {/* Netlify hidden input for form name */}
                  <input type="hidden" name="form-name" value="contact" />
                  {/* Netlify honeypot field */}
                  <input type="hidden" name="bot-field" />
                  <Grid container spacing={3}>
                    {/* Full Name */}
                    <Grid item size={{ xs: 12, md: 6 }}>
                      <Box>
                        <Typography
                          variant="body2"
                          sx={{
                            color: theme.palette.text.secondary,
                            mb: 1,
                            fontSize: "0.9rem",
                            fontWeight: 500,
                          }}
                        >
                          Full name
                        </Typography>
                        <TextField
                          fullWidth
                          name="fullName"
                          required
                          variant="outlined"
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              backgroundColor: "#f5f5f5",
                              borderRadius: 2,
                              border: "none",
                              "& fieldset": {
                                border: "none",
                              },
                              "&:hover fieldset": {
                                border: "none",
                              },
                              "&.Mui-focused fieldset": {
                                border: "none",
                              },
                              "& input": {
                                py: 2,
                                fontSize: "1rem",
                                fontWeight: 600,
                                color: "#000",
                              },
                            },
                          }}
                        />
                      </Box>
                    </Grid>

                    {/* Phone */}
                    <Grid item size={{ xs: 12, md: 6 }}>
                      <Box>
                        <Typography
                          variant="body2"
                          sx={{
                            color: theme.palette.text.secondary,
                            mb: 1,
                            fontSize: "0.9rem",
                            fontWeight: 500,
                          }}
                        >
                          Phone
                        </Typography>
                        <TextField
                          fullWidth
                          name="phone"
                          required
                          variant="outlined"
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              backgroundColor: "#f5f5f5",
                              borderRadius: 2,
                              border: "none",
                              "& fieldset": {
                                border: "none",
                              },
                              "&:hover fieldset": {
                                border: "none",
                              },
                              "&.Mui-focused fieldset": {
                                border: "none",
                              },
                              "& input": {
                                py: 2,
                                fontSize: "1rem",
                                fontWeight: 600,
                                color: "#000",
                              },
                            },
                          }}
                        />
                      </Box>
                    </Grid>

                    {/* Email */}
                    <Grid item size={12}>
                      <Box>
                        <Typography
                          variant="body2"
                          sx={{
                            color: theme.palette.text.secondary,
                            mb: 1,
                            fontSize: "0.9rem",
                            fontWeight: 500,
                          }}
                        >
                          Email
                        </Typography>
                        <TextField
                          fullWidth
                          name="email"
                          type="email"
                          required
                          variant="outlined"
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              backgroundColor: "#f5f5f5",
                              borderRadius: 2,
                              border: "none",
                              "& fieldset": {
                                border: "none",
                              },
                              "&:hover fieldset": {
                                border: "none",
                              },
                              "&.Mui-focused fieldset": {
                                border: "none",
                              },
                              "& input": {
                                py: 2,
                                fontSize: "1rem",
                                fontWeight: 600,
                                color: "#000",
                              },
                            },
                          }}
                        />
                      </Box>
                    </Grid>

                    {/* Message */}
                    <Grid item size={12}>
                      <Box>
                        <Typography
                          variant="body2"
                          sx={{
                            color: theme.palette.text.secondary,
                            mb: 1,
                            fontSize: "0.9rem",
                            fontWeight: 500,
                          }}
                        >
                          Message
                        </Typography>
                        <TextField
                          fullWidth
                          multiline
                          rows={4}
                          name="message"
                          required
                          variant="outlined"
                          placeholder="Type your message here"
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              backgroundColor: "#f5f5f5",
                              borderRadius: 2,
                              border: "none",
                              "& fieldset": {
                                border: "none",
                              },
                              "&:hover fieldset": {
                                border: "none",
                              },
                              "&.Mui-focused fieldset": {
                                border: "none",
                              },
                              "& textarea": {
                                fontSize: "1rem",
                                fontWeight: 600,
                                color: "#000",
                              },
                            },
                          }}
                        />
                      </Box>
                    </Grid>

                    {/* Submit Button */}
                    <Grid item size={12}>
                      <Button
                        fullWidth
                        type="submit"
                        sx={{
                          backgroundColor: "#000",
                          color: "white",
                          py: 2.5,
                          borderRadius: 6,
                          fontSize: "1.1rem",
                          fontWeight: 600,
                          textTransform: "none",
                          fontFamily: "Monument-Private",
                          "&:hover": {
                            backgroundColor: "#333",
                            transform: "translateY(-1px)",
                            boxShadow: "0 6px 20px rgba(0,0,0,0.2)",
                          },
                          transition: "all 0.3s ease",
                        }}
                      >
                        Send message
                      </Button>
                    </Grid>
                  </Grid>
                </form>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>
      <Footer />
    </>
  );
};

export default Contact;
